import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/vigorbuy-data';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';
import { createArticleSchema } from '@/utils/schema-utils';

export default function VigorBuyCategories() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <JsonLd data={createArticleSchema({
        path: '/vigorbuy-spreadsheet-categories',
        headline: 'VigorBuy Spreadsheet Categories: Complete Product Directory for 2026',
        description: 'A comprehensive walkthrough of all 14 VigorBuy Spreadsheet product categories — from Shoes and Hoodies to Accessories, Bags, and Perfumes. Discover what each category offers and how to find the best products for your needs.',
        imageUrl: 'https://readdy.ai/api/search-image?query=Organized%20colorful%20product%20display%20flat%20lay%20with%20shoes%20hoodies%20tshirts%20jackets%20accessories%20and%20bags%20arranged%20in%20neat%20grid%20pattern%20on%20warm%20wooden%20surface%2C%20soft%20natural%20lighting%2C%20editorial%20catalog%20photography%2C%20clean%20modern%20retail%20aesthetic&width=1200&height=600&seq=cat-guide-hero&orientation=landscape',
      })} />
      <Navbar />
      <main>
        <section className="w-full pt-24 pb-12 md:pt-28 md:pb-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-4">
              <Link to="/" className="text-foreground-400 text-xs hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-foreground-300 text-xs"></i>
              <span className="text-foreground-600 text-xs">Category Guide</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              VigorBuy Spreadsheet Categories: Complete Product Directory for 2026
            </h1>
            <p className="text-foreground-500 text-sm md:text-base mb-6 leading-relaxed">
              A comprehensive walkthrough of all 14 VigorBuy Spreadsheet product categories — from Shoes and Hoodies to Accessories, Bags, and Perfumes. Discover what each category offers and how to find the best products for your needs.
            </p>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i> Updated July 2026</span>
              <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> 15 min read</span>
              <span className="flex items-center gap-1.5"><i className="ri-user-line"></i> By VigorBuy Team</span>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6 -mt-6">
            <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Organized%20colorful%20product%20display%20flat%20lay%20with%20shoes%20hoodies%20tshirts%20jackets%20accessories%20and%20bags%20arranged%20in%20neat%20grid%20pattern%20on%20warm%20wooden%20surface%2C%20soft%20natural%20lighting%2C%20editorial%20catalog%20photography%2C%20clean%20modern%20retail%20aesthetic&width=1200&height=600&seq=cat-guide-hero&orientation=landscape"
                alt="VigorBuy Spreadsheet categories directory"
                title="VigorBuy Spreadsheet Product Categories Guide"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="prose-custom text-foreground-700 leading-relaxed text-sm md:text-base space-y-8">

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Why Category Organization Matters for Product Discovery
                </h2>
                <p className="mb-4">
                  One of the biggest advantages of the <strong className="text-foreground-900">VigorBuy Spreadsheet</strong> is how thoughtfully it organizes products into clearly defined categories. Rather than dumping thousands of items into a single chaotic list, the spreadsheet groups products by type, style, and function — making it dramatically easier to find exactly what you are looking for without getting lost in irrelevant listings.
                </p>
                <p className="mb-4">
                  In this guide, we will walk through every single category available in the <strong className="text-foreground-900">VigorBuy Spreadsheet</strong>, explaining what you can expect to find in each one and sharing tips for getting the most out of your browsing experience. Whether you are hunting for a specific item or just want to explore what is available, understanding the category structure will save you time and help you discover products more effectively.
                </p>
                <p>
                  Each category connects directly to a curated collection on the{' '}
                  <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-primary-500 font-semibold hover:text-primary-600 transition-colors cursor-pointer">
                    main VigorBuy Spreadsheet website
                  </a>, so you can seamlessly transition from browsing to exploring full product details with a single click.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Complete Category Breakdown
                </h2>

                {categories.map((cat, idx) => (
                  <div key={idx} className="mb-8">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                      {idx + 1}. {cat.name}
                    </h3>
                    <p className="mb-3">
                      The <strong className="text-foreground-900">{cat.name}</strong> category in the <strong className="text-foreground-900">VigorBuy Spreadsheet</strong> features a carefully curated selection of trending products that match this specific theme. Each entry includes relevant details to help you evaluate options before diving deeper into individual product pages. The category is regularly updated to reflect current trends and popular styles, so you can always find fresh content when you return.
                    </p>
                    <p className="mb-3">
                      Shoppers frequently visit this category because it saves significant time compared to searching across multiple websites. Instead of opening dozens of browser tabs and manually comparing products from different sources, you can survey all available options in one organized view and make faster, more confident decisions.
                    </p>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 text-primary-500 font-medium text-sm hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      Browse {cat.name} Collection
                      <i className="ri-arrow-right-up-line text-xs"></i>
                    </a>
                  </div>
                ))}
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Tips for Navigating VigorBuy Spreadsheet Categories
                </h2>
                <p className="mb-4">
                  To get the most value from the <strong className="text-foreground-900">VigorBuy Spreadsheet</strong> category system, keep these practical tips in mind during your browsing sessions.
                </p>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mt-5 mb-3">
                  Start Broad, Then Narrow Down
                </h3>
                <p className="mb-4">
                  Begin with the category that most broadly matches your interest — for example, start with Shoes rather than immediately trying to find a specific sneaker model. Browsing the broader category first gives you a better overview of what is available and may introduce you to products you would not have discovered otherwise.
                </p>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mt-5 mb-3">
                  Use Multiple Categories for Comparison
                </h3>
                <p className="mb-4">
                  If you are shopping for a complete outfit or complementary items, explore related categories side by side. For instance, if you are looking at Jackets, you might also want to browse the Hoodies and Sweaters category simultaneously to compare styles and find the best match for your needs.
                </p>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mt-5 mb-3">
                  Check Back Regularly
                </h3>
                <p className="mb-4">
                  The <strong className="text-foreground-900">VigorBuy Spreadsheet</strong> categories are regularly refreshed with new product entries. Making a habit of checking your favorite categories every week or two ensures you never miss out on newly added products and trending items that might align with your interests.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Final Thoughts on VigorBuy Spreadsheet Categories
                </h2>
                <p className="mb-4">
                  The category system is one of the most powerful features of the <strong className="text-foreground-900">VigorBuy Spreadsheet</strong>. It transforms what could be an overwhelming wall of products into an intuitive, navigable directory that anyone can use comfortably — whether you are a first-time visitor or a seasoned product research enthusiast.
                </p>
                <p>
                  Ready to start exploring?{' '}
                  <Link to="/" className="text-primary-500 font-semibold hover:text-primary-600 transition-colors cursor-pointer">
                    Return to the homepage
                  </Link>{' '}
                  and browse all categories, or{' '}
                  <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-primary-500 font-semibold hover:text-primary-600 transition-colors cursor-pointer">
                    visit the main website
                  </a>{' '}
                  to dive straight into the complete product collections.
                </p>
              </section>

            </div>
          </div>
        </article>

        <section className="w-full py-12 bg-primary-500">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
              Explore All Categories Now
            </h2>
            <p className="text-primary-100 text-sm md:text-base mb-6 max-w-xl mx-auto">
              Fourteen organized categories with curated product collections. Start discovering products that match your style.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-background-50 text-primary-600 font-semibold text-base hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                Visit Main Website
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-primary-600 text-background-50 font-semibold text-base hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer border border-primary-400">
                Back to Homepage
                <i className="ri-home-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}